import React from 'react'

import { Features } from 'generated/featureDefinitions'
import {
  AttendanceManualCompensation,
  AttendanceSidepanelCompensationBlockFragment,
  BankOfHoursBreakdownPanelDocument,
} from 'generated/resources/graphql'
import { Moment } from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'

import CompensationResponse from 'modules/attendance/components/ClockIn/EmployeeTimeTrackingBreakdown/Compensation/Modals/CompensationResponse'
import CreateTimeBlockCompensationWizard from 'modules/attendance/components/ClockIn/EmployeeTimeTrackingBreakdown/Compensation/Modals/CreateTimeBlockCompensationWizard'
import CreateTimeBlockSupplementWizard from 'modules/attendance/components/ClockIn/EmployeeTimeTrackingBreakdown/Compensation/Modals/CreateTimeBlockSupplementWizard'
import { useAttendanceFeatures } from 'modules/attendance/lib/attendanceFeaturesContext'
import { useTimesheetData } from 'modules/attendance/lib/timesheetDataContext'

type Props = {
  employeeId: number
  timeSheetData: ReturnType<typeof useTimesheetData>
  timeSheetDate: Moment
  closePanel: () => void
  setOpenModal: (open: boolean) => void
  openModal: boolean
  selectedCompensationBlocks?: AttendanceSidepanelCompensationBlockFragment[]
}

const CompensationModalWrapper = ({
  employeeId,
  timeSheetData,
  timeSheetDate,
  closePanel,
  openModal,
  setOpenModal,
  selectedCompensationBlocks = [],
}: Props) => {
  const [openResponseModal, setOpenResponseModal] = React.useState(false)

  const { featureAvailability: hasCompensations } = useAttendanceFeatures(
    Features.compensations_management
  )

  const [responseData, setResponseData] = React.useState<
    AttendanceManualCompensation | undefined | null
  >()

  const getResponseData = (response: AttendanceManualCompensation | undefined | null) => {
    setResponseData(response)

    if (response) setOpenResponseModal(true)
  }

  const { timeBlocksBalance, loading: isLoading } = timeSheetData

  const { company } = useCurrentSession()

  const { loading, data } = useQuery(BankOfHoursBreakdownPanelDocument, {
    variables: {
      companyId: company.id,
    },
  })

  const reimbursements = data?.compensations.reimbursablePayrollConceptsConnection.nodes ?? []
  const timeRangeCategories = data?.timeSettings.customTimeRangeCategoriesConnection.nodes ?? []
  const payrollConcepts = data?.payroll.conceptsConnection.nodes ?? []

  return (
    <>
      {openModal && !hasCompensations && (
        <CreateTimeBlockSupplementWizard
          employeeId={employeeId}
          getResponseData={getResponseData}
          loading={isLoading}
          onCancel={() => setOpenModal(false)}
          timeBlocksBalance={timeBlocksBalance}
          timeSheetDate={timeSheetDate}
          selectedCompensationBlocks={selectedCompensationBlocks}
        />
      )}
      {openModal && hasCompensations && (
        <CreateTimeBlockCompensationWizard
          employeeId={employeeId}
          getResponseData={getResponseData}
          loading={isLoading || loading}
          onCancel={() => setOpenModal(false)}
          timeBlocksBalance={timeBlocksBalance}
          timeSheetDate={timeSheetDate}
          reimbursements={reimbursements}
          timeRangeCategories={timeRangeCategories}
          payrollConcepts={payrollConcepts}
          selectedCompensationBlocks={selectedCompensationBlocks}
        />
      )}

      {openResponseModal && (
        <CompensationResponse
          response={responseData}
          onCloseModal={() => {
            setOpenResponseModal(false)
            closePanel()
          }}
        />
      )}
    </>
  )
}

export default CompensationModalWrapper
